import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioTable = ({ assets, onAssetSelect, onBatchRevalue }) => {
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'lastValuation', direction: 'desc' });

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAssets(assets?.map(asset => asset?.id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (assetId, checked) => {
    if (checked) {
      setSelectedAssets([...selectedAssets, assetId]);
    } else {
      setSelectedAssets(selectedAssets?.filter(id => id !== assetId));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAssets = [...assets]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const getRiskBadge = (ltv) => {
    if (ltv >= 90) return { label: 'High Risk', color: 'bg-destructive text-destructive-foreground' };
    if (ltv >= 75) return { label: 'Medium Risk', color: 'bg-warning text-warning-foreground' };
    return { label: 'Low Risk', color: 'bg-success text-success-foreground' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Portfolio Assets</h2>
          <div className="flex items-center space-x-3">
            {selectedAssets?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBatchRevalue(selectedAssets)}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Revalue Selected ({selectedAssets?.length})
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
            >
              Add Asset
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="p-4 text-left">
                <input
                  type="checkbox"
                  checked={selectedAssets?.length === assets?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">
                <button
                  onClick={() => handleSort('assetName')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Asset</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">
                <button
                  onClick={() => handleSort('bookValue')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Book Value</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">
                <button
                  onClick={() => handleSort('marketValue')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Market Value</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">
                <button
                  onClick={() => handleSort('ltv')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>LTV</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">Risk Level</th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">
                <button
                  onClick={() => handleSort('lastValuation')}
                  className="flex items-center space-x-1 hover:text-text-primary"
                >
                  <span>Last Valuation</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-medium text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAssets?.map((asset) => {
              const riskBadge = getRiskBadge(asset?.ltv);
              return (
                <tr key={asset?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedAssets?.includes(asset?.id)}
                      onChange={(e) => handleSelectAsset(asset?.id, e?.target?.checked)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="p-4">
                    <div>
                      <div className="font-medium text-text-primary">{asset?.assetName}</div>
                      <div className="text-sm text-text-secondary">{asset?.assetType}</div>
                    </div>
                  </td>
                  <td className="p-4 text-text-primary font-medium">
                    {formatCurrency(asset?.bookValue)}
                  </td>
                  <td className="p-4">
                    <div className="text-text-primary font-medium">
                      {formatCurrency(asset?.marketValue)}
                    </div>
                    <div className={`text-sm ${asset?.valueChange >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {asset?.valueChange >= 0 ? '+' : ''}{asset?.valueChange}%
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-text-primary font-medium">{asset?.ltv}%</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskBadge?.color}`}>
                      {riskBadge?.label}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-text-secondary">
                    {formatDate(asset?.lastValuation)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAssetSelect(asset)}
                        iconName="Eye"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="RefreshCw"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioTable;